"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.UserService = exports.NoConsentError = void 0;
// eslint-disable-next-line max-classes-per-file
const isEqual_1 = __importDefault(require("lodash/isEqual"));
const superstruct_1 = require("superstruct");
const p_queue_1 = __importDefault(require("p-queue"));
const Common_1 = require("./Common");
const Logger_1 = __importDefault(require("./Logger"));
const Utils_1 = require("./Utils");
const UserConsentTypes_1 = require("./types/UserConsentTypes");
class NoConsentError extends Error {
    constructor() {
        super("There is no User Consent");
    }
}
exports.NoConsentError = NoConsentError;
class UserService {
    constructor(contextService, localDataService, restService, cookieService) {
        this.contextService = contextService;
        this.localDataService = localDataService;
        this.restService = restService;
        this.cookieService = cookieService;
        this.pQueue = new p_queue_1.default({ concurrency: 1 });
        this.consentChangedEventQueue = [];
        this.onUserChanging = null;
        this.saveUserConsentDetectToken = this.saveUserConsentDetectToken.bind(this);
    }
    get unnamedUser() {
        const cookieUnnamedUserId = this.cookieService.unnamedUserId;
        if (cookieUnnamedUserId) {
            return {
                id: cookieUnnamedUserId,
                provider: Common_1.USER_IDENTITY_PROVIDER.COOKIE,
            };
        }
        return {
            id: this.localDataService.unnamedUserId,
            provider: Common_1.USER_IDENTITY_PROVIDER.SDK,
        };
    }
    pushConsentChangedEventQueue(event) {
        this.consentChangedEventQueue.push(event);
    }
    clear() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.pQueue.add(() => __awaiter(this, void 0, void 0, function* () {
                this.localDataService.isNamedUser = false;
                this.contextService.context.user = undefined;
                this.contextService.context.hubId = undefined;
                this.contextService.context.hubUrn = undefined;
                this.contextService.context.projectId = undefined;
                this.contextService.context.projectUrn = undefined;
                this.contextService.context.userIntent = undefined;
                this.contextService.context.facets.user = undefined;
            }));
        });
    }
    setup(identify) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.pQueue.add(() => __awaiter(this, void 0, void 0, function* () {
                try {
                    if (identify) {
                        yield this.identifyUser();
                    }
                    else {
                        yield this.restoreUser();
                    }
                }
                catch (e) {
                    Logger_1.default.error("ERROR Setup User", e);
                }
                finally {
                    Logger_1.default.debug("END Setup User", this.contextService.context);
                }
            }));
        });
    }
    restoreUser() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            Logger_1.default.debug("Restoring user");
            if (this.localDataService.isNamedUser) {
                const userConsent = (_a = (yield this.setupNamedUserDetectToken())) !== null && _a !== void 0 ? _a : (yield this.setupLocalUnnamedUser());
                if (userConsent == null) {
                    Logger_1.default.debug("There is no cached User Consent to restore");
                }
                else {
                    this.reactToUserConsent(userConsent);
                }
            }
            else {
                const userConsent = yield this.setupLocalUnnamedUser();
                if (userConsent != null) {
                    this.reactToUserConsent(userConsent);
                }
            }
        });
    }
    identifyUser() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            Logger_1.default.debug("Identify User");
            const userConsent = (_b = (_a = (yield this.setupNamedUserDetectToken())) !== null && _a !== void 0 ? _a : (yield this.setupLocalUnnamedUser())) !== null && _b !== void 0 ? _b : (yield this.setupRemoteUnnamedUser());
            this.reactToUserConsent(userConsent);
        });
    }
    reactToUserConsent(userConsent) {
        var _a, _b;
        Logger_1.default.debug("React to User Consent", userConsent);
        this.contextService.writeUserConsentPreferences(userConsent.preferences);
        this.contextService.writeUserConsentCopy(userConsent.consentCopy);
        const useCallbackFlow = this.contextService.context.tokenInfo == null;
        if (useCallbackFlow && UserService.shouldShowUserConsentDialog(userConsent)) {
            (_b = (_a = this.contextService.context.callback).buildUserConsentDialog) === null || _b === void 0 ? void 0 : _b.call(_a, userConsent.preferences, userConsent.consentCopy, this.saveUserConsentDetectToken);
        }
    }
    detectToken() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            if (this.contextService.context.tokenInfo != null) {
                return this.contextService.context.tokenInfo;
            }
            try {
                const token = yield ((_b = (_a = this.contextService.context.callback).retrieveUserToken) === null || _b === void 0 ? void 0 : _b.call(_a));
                const tokenInfo = yield this.parseTokenInfo(token);
                if (tokenInfo == null) {
                    Logger_1.default.debug("There is no token provider or its token");
                    return undefined;
                }
                if (tokenInfo instanceof Error) {
                    Logger_1.default.error(tokenInfo.message, tokenInfo);
                    return undefined;
                }
                return tokenInfo;
            }
            catch (e) {
                Logger_1.default.error("Error invoking user token provider", e);
                return undefined;
            }
        });
    }
    setupNamedUserDetectToken() {
        return __awaiter(this, void 0, void 0, function* () {
            const tokenInfo = yield this.detectToken();
            return tokenInfo != null ? this.setupNamedUser(tokenInfo) : undefined;
        });
    }
    setupNamedUser(tokenInfo) {
        return __awaiter(this, void 0, void 0, function* () {
            Logger_1.default.debug("Trying Setup Named User");
            this.contextService.context.user = {
                provider_name: Common_1.USER_IDENTITY_PROVIDER.O2,
                user_id: tokenInfo.userId,
                logged_in: true,
            };
            const { id: unnamedUserId } = this.unnamedUser;
            if (unnamedUserId) {
                this.contextService.context.facets.alias = {
                    new_id: tokenInfo.userId,
                    previous_id: unnamedUserId,
                };
            }
            this.localDataService.isNamedUser = true;
            const userConsent = (yield this.restService.doGetNamedUserConsent(tokenInfo)).data;
            Logger_1.default.debug("Done Setup Named User", userConsent);
            return userConsent;
        });
    }
    setupLocalUnnamedUser() {
        return __awaiter(this, void 0, void 0, function* () {
            Logger_1.default.debug("Trying Setup Local Unnamed User");
            const { id: unnamedUserId, provider } = this.unnamedUser;
            const preferences = this.localDataService.userConsentPreferences;
            const consentCopy = this.localDataService.userConsentCopy;
            if (!unnamedUserId || !preferences || !consentCopy) {
                Logger_1.default.debug("There is no cached Unnamed User");
                return undefined;
            }
            this.contextService.context.user = {
                provider_name: provider,
                user_id: unnamedUserId,
                logged_in: false,
            };
            const userConsent = {
                userId: unnamedUserId,
                userActionRequired: false,
                preferences,
                consentCopy,
            };
            this.localDataService.isNamedUser = false;
            Logger_1.default.debug("Done Setup Local Unnamed User", userConsent);
            return userConsent;
        });
    }
    setupRemoteUnnamedUser() {
        return __awaiter(this, void 0, void 0, function* () {
            Logger_1.default.debug("Trying Setup Remote Unnamed User");
            const consentCopy = yield this.getUserConsentCopy();
            const sdkUserId = (0, Utils_1.genGuid)();
            const cookieUserId = this.cookieService.unnamedUserId;
            this.contextService.context.user = cookieUserId
                ? {
                    provider_name: Common_1.USER_IDENTITY_PROVIDER.COOKIE,
                    user_id: cookieUserId,
                    logged_in: false,
                }
                : {
                    provider_name: Common_1.USER_IDENTITY_PROVIDER.SDK,
                    user_id: sdkUserId,
                    logged_in: false,
                };
            const userConsent = {
                userId: this.contextService.context.user.user_id,
                userActionRequired: false,
                preferences: UserService.userConsentCopyToPreferences(consentCopy),
                consentCopy,
            };
            this.localDataService.unnamedUserId = sdkUserId;
            this.localDataService.userConsentCopy = userConsent.consentCopy;
            this.localDataService.userConsentPreferences = userConsent.preferences;
            this.localDataService.isNamedUser = false;
            Logger_1.default.debug("Done Setup Remote Unnamed User", userConsent);
            return userConsent;
        });
    }
    parseTokenInfo(userToken) {
        return __awaiter(this, void 0, void 0, function* () {
            if (userToken == null) {
                return undefined;
            }
            Logger_1.default.debug("Parsing user token, please wait...");
            try {
                const tokenParts = userToken.split(".");
                const decodedInfo = atob(tokenParts[1]);
                const parsedInfo = JSON.parse(decodedInfo);
                Logger_1.default.debug("Parsed user token", parsedInfo);
                return { userId: parsedInfo.userid, userToken };
            }
            catch (e) {
                Logger_1.default.error("Error while parsing the user token", e);
                return new Error(Common_1.ERROR_MESSAGE.invalidUserToken);
            }
        });
    }
    getCurrentUserConsentPreferences() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.pQueue.add(() => __awaiter(this, void 0, void 0, function* () { return this.contextService.context.userConsentPreferences; }));
        });
    }
    getCurrentUserConsentCopy() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.pQueue.add(() => __awaiter(this, void 0, void 0, function* () { return this.contextService.context.userConsentCopy; }));
        });
    }
    getUserConsentPreferences(tokenInfo) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.pQueue.add(() => __awaiter(this, void 0, void 0, function* () {
                var _a;
                if (tokenInfo != null) {
                    Logger_1.default.debug("Fetching preferences for user", tokenInfo.userId);
                    return (yield this.restService.doGetNamedUserConsent(tokenInfo)).data.preferences;
                }
                Logger_1.default.debug("Fetching preferences for unnamed user");
                return ((_a = this.localDataService.userConsentPreferences) !== null && _a !== void 0 ? _a : UserService.userConsentCopyToPreferences(yield this.getUserConsentCopy()));
            }));
        });
    }
    getCurrentUser() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.pQueue.add(() => __awaiter(this, void 0, void 0, function* () { return this.contextService.user; }));
        });
    }
    loadUser(tokenInfo) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.pQueue.add(() => __awaiter(this, void 0, void 0, function* () {
                var _a, _b, _c, _d, _e;
                const isNamedUser = tokenInfo != null;
                if (
                // Different user ID
                (isNamedUser && tokenInfo.userId !== ((_a = this.contextService.context.user) === null || _a === void 0 ? void 0 : _a.user_id)) ||
                    // Load unnamed user; current user is not unnamed user
                    (!isNamedUser && ((_b = this.contextService.context.user) === null || _b === void 0 ? void 0 : _b.provider_name) !== Common_1.USER_IDENTITY_PROVIDER.SDK)) {
                    (_c = this.onUserChanging) === null || _c === void 0 ? void 0 : _c.call(this, (_d = this.contextService.context.user) === null || _d === void 0 ? void 0 : _d.user_id, tokenInfo === null || tokenInfo === void 0 ? void 0 : tokenInfo.userId);
                }
                const userConsent = isNamedUser
                    ? yield this.setupNamedUser(tokenInfo)
                    : (_e = (yield this.setupLocalUnnamedUser())) !== null && _e !== void 0 ? _e : (yield this.setupRemoteUnnamedUser());
                /** @todo exact behavior is not well defined at moment. May want to move this assertion earlier in setup*User() so that the user context is not changed. */
                (0, superstruct_1.assert)(userConsent, UserConsentTypes_1.UserConsentStruct);
                this.contextService.context.tokenInfo = tokenInfo;
                this.contextService.writeUserConsentPreferences(userConsent.preferences);
                this.contextService.writeUserConsentCopy(userConsent.consentCopy);
                return userConsent;
            }));
        });
    }
    getUserConsentCopy() {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield this.restService.doGetUnnamedUserConsent()).data.consentCopy;
        });
    }
    readConsentMetric() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.pQueue.add(() => __awaiter(this, void 0, void 0, function* () {
                const preferences = this.contextService.readUserConsentPreferences();
                if (preferences == null) {
                    return new NoConsentError();
                }
                const consentOptIn = UserService.isUserConsentOptIn(preferences);
                if (consentOptIn) {
                    return preferences.map((p) => ({
                        consentId: p.consentId,
                        optIn: p.optIn,
                    }));
                }
                return new NoConsentError();
            }));
        });
    }
    saveUserConsentDetectToken(preferences) {
        return __awaiter(this, void 0, void 0, function* () {
            const tokenInfo = yield this.detectToken();
            return this.saveUserConsent(preferences, tokenInfo);
        });
    }
    saveUserConsent(preferences, tokenInfo) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.pQueue.add(() => __awaiter(this, void 0, void 0, function* () {
                Logger_1.default.debug("Validate input User Consent", preferences);
                (0, superstruct_1.assert)(preferences, (0, superstruct_1.array)(UserConsentTypes_1.UserConsentPreferenceInputStruct));
                const localPreferences = this.contextService.readUserConsentPreferences();
                const inputPreferencesMap = new Map(preferences.map((item) => [item.consentId, item.optIn]));
                const localPreferencesMap = new Map(localPreferences === null || localPreferences === void 0 ? void 0 : localPreferences.map((item) => [item.consentId, item.optIn]));
                const redundant = Array.from(inputPreferencesMap.keys()).filter((key) => !localPreferencesMap.has(key));
                if (redundant.length) {
                    throw new Error(`Redundant preferences ${redundant}`);
                }
                const lacking = Array.from(localPreferencesMap.keys()).filter((key) => !inputPreferencesMap.has(key));
                if (lacking.length) {
                    throw new Error(`Lacking preferences ${lacking}`);
                }
                if ((0, isEqual_1.default)(inputPreferencesMap, localPreferencesMap)) {
                    Logger_1.default.debug("There is no change to update User Consent");
                    return;
                }
                let updatedPreferences;
                if (tokenInfo != null) {
                    Logger_1.default.debug("Call Rest API update User Consent", preferences);
                    const response = yield this.restService.doPostUserConsent(preferences, tokenInfo);
                    const lastUpdated = response.data;
                    updatedPreferences = preferences.map((p) => (Object.assign(Object.assign({}, p), { lastUpdated })));
                }
                else {
                    Logger_1.default.debug("Local unnamed update User Consent", preferences);
                    const lastUpdated = Date.now();
                    updatedPreferences = preferences.map((p) => (Object.assign(Object.assign({}, p), { lastUpdated })));
                    this.localDataService.userConsentPreferences = updatedPreferences;
                }
                this.contextService.writeUserConsentPreferences(updatedPreferences);
                Logger_1.default.debug("Send Pending Event for User Consent Change");
                while (this.consentChangedEventQueue.length > 0) {
                    const event = this.consentChangedEventQueue.pop();
                    if (event != null) {
                        this.contextService.sendEventInQueue(event[0], event[1]);
                    }
                }
                Logger_1.default.debug("Send METRICS_SETTING for User Consent Change");
                this.contextService.sendEventInQueue("METRICS_SETTING", {
                    component: {
                        type: "metrics",
                        name: "optin_metrics",
                        attributes: { optin_status: UserService.isUserConsentOptIn(preferences) ? "opt-in" : "opt-out" },
                    },
                }, preferences);
            }));
        });
    }
    setUserIntent(value) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.pQueue.add(() => {
                this.contextService.context.userIntent = value;
            });
        });
    }
    setCurrentUserConsentPreferences(preferences) {
        return __awaiter(this, void 0, void 0, function* () {
            const currentUser = yield this.getCurrentUser();
            // indicates user not yet loaded or set
            if (!currentUser) {
                Logger_1.default.error(Common_1.ERROR_MESSAGE.userConsentNotLoaded);
                return;
            }
            this.saveUserConsentDetectToken(preferences);
        });
    }
    static userConsentCopyToPreferences(data) {
        return data.copies.map((copy) => ({
            consentId: copy.consentId,
            optIn: null,
            lastUpdated: null,
        }));
    }
    static isUserConsentOptIn(preferences) {
        return preferences.every((p) => p.optIn != null) && preferences.some((p) => p.optIn === true);
    }
    static shouldShowUserConsentDialog(userConsent) {
        return userConsent.userActionRequired || userConsent.preferences.some((p) => p.optIn == null);
    }
}
exports.UserService = UserService;
